'use client';

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { trackFathomEvent } from '@/lib/fathom/trackFathomEvent';
import type { AllFathomEventNames } from '@/lib/fathom/fathom-events';
import type { FormErrorStatus } from '@/lib/forms/form-error-messages';

/**
 * Form funnel + breakage monitoring for the strict forms (trial / demo / contact).
 *
 * Fathom gets the counts the daily breakage check and the weekly funnel read
 * (tools/revenue-pages): how many people start a form, and how often a submit ends on an
 * error card or the security check fails. Sentry gets the *why* for Turnstile failures
 * (cause + browser), which a property-less Fathom event can't carry.
 */

/** Error-card statuses with a funnel event. `work-email-required` has its own per-reason
 * events (work-email-tracking.ts). */
export type FunnelErrorStatus = Exclude<FormErrorStatus, 'work-email-required'>;

const FUNNEL_ERROR_STATUSES: readonly FunnelErrorStatus[] = [
  'verification-required',
  'verification-failed',
  'submission-failed',
];

/** A strict form's co-located funnel events (see `FATHOM_EVENTS.<form>.hero.funnel`). */
export interface FormFunnelEvents {
  started: AllFathomEventNames;
  turnstile_error: AllFathomEventNames;
  error: Record<FunnelErrorStatus, AllFathomEventNames>;
}

/** Why the Turnstile widget failed: a Cloudflare error code, or one of our own causes. */
export type TurnstileErrorCause = 'script-blocked' | 'load-timeout' | 'unsupported' | (string & {});

/** Hosts whose Turnstile failures mean something. Previews and localhost always fail (the
 * site key only allows develocity.ai), so reporting there would bury real breakage. */
const PRODUCTION_HOSTS = new Set(['develocity.ai', 'www.develocity.ai']);

export function isProductionHost(hostname: string): boolean {
  return PRODUCTION_HOSTS.has(hostname);
}

/** Hosts whose error cards count: production, plus localhost for the e2e suite (Fathom
 * itself ignores localhost). Not previews: a hand-typed `?error=` there would trip the
 * daily breakage alerts. */
export function countsErrorCards(hostname: string): boolean {
  return isProductionHost(hostname) || hostname === 'localhost';
}

/** How long Turnstile must stay in `error` before it counts: the widget retries itself,
 * and a slow script trips the load watchdog and then recovers. */
export const TURNSTILE_ERROR_GRACE_MS = 10_000;

/** The error-card event for `?error=<status>`, or null for a work-email rejection, an
 * unknown status (hand-crafted URL) or a repeated param. Pure. */
export function formErrorCardEvent(
  errorStatus: string | string[] | undefined,
  events: FormFunnelEvents,
): AllFathomEventNames | null {
  return typeof errorStatus === 'string' && (FUNNEL_ERROR_STATUSES as readonly string[]).includes(errorStatus)
    ? events.error[errorStatus as FunnelErrorStatus]
    : null;
}

/** Fire the error-card event once per mount when the card renders. `isShown` must mirror
 * the page's own error-card condition. No dedupe across reloads: a reload re-counts, so
 * the monitor reads unique conversions. Production (and localhost) only. */
export function useFormErrorCardEvent(
  isShown: boolean,
  errorStatus: string | string[] | undefined,
  events: FormFunnelEvents,
): void {
  const firedRef = useRef(false);
  const event = isShown ? formErrorCardEvent(errorStatus, events) : null;
  useEffect(() => {
    if (!event || firedRef.current || !countsErrorCards(window.location.hostname)) return;
    firedRef.current = true;
    trackFathomEvent(event);
  }, [event]);
}

/** Whether an `input`/`change` event target is a real form field a person fills in —
 * not a hidden field (UTM, Clearbit, `_formId`) or the Turnstile token. Pure. */
export function isFormStartTarget(target: EventTarget | null): boolean {
  if (
    !(
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement
    )
  ) {
    return false;
  }
  if (target instanceof HTMLInputElement && target.type === 'hidden') return false;
  return target.name !== 'cf-turnstile-response';
}

/** Report a Turnstile failure: one Fathom event (the count) and one Sentry message (the
 * cause; Sentry adds browser + OS). Production hosts only. Never throws. */
export function reportTurnstileError(
  formId: string,
  event: AllFathomEventNames,
  cause: TurnstileErrorCause | undefined,
): void {
  if (typeof window === 'undefined' || !isProductionHost(window.location.hostname)) return;
  trackFathomEvent(event);
  try {
    Sentry.captureMessage(`Turnstile failed on the ${formId} form`, {
      level: 'warning',
      tags: { form: formId, turnstile_cause: cause ?? 'unknown' },
      // Group by form + cause so one blocked-script wave is one issue, not thousands.
      fingerprint: ['turnstile-error', formId, cause ?? 'unknown'],
      extra: { cookieEnabled: navigator.cookieEnabled, language: navigator.language },
    });
  } catch {
    // Monitoring must never break the form.
  }
}
